import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Youtube, Music, Instagram } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useContent } from "@/hooks/useContent";

export default function Footer({ content: contentProp }) {
  const { t } = useLanguage();
  const fetched = useContent();
  const content = contentProp || fetched;
  const booking = content?.footer_booking || t.footer.booking;
  const phone = content?.footer_phone || t.footer.phone;
  const bookingEmail = content?.footer_booking_email || "simona@pinkofein.cz";
  const pressName = content?.footer_press_name || "Monika Soukupová";
  const pressPhone = content?.footer_press_phone || "+420 602 65 90 14";
  const pressEmail = content?.footer_press_email || "";
  const production = content?.footer_production || t.footer.production;

  const telHref = (p) => `tel:${(p || "").replace(/[\s()-]/g, "")}`;

  return (
    <footer style={{ background: "#050505", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      {/* Big REBELKY text */}
      <div className="overflow-hidden" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "2rem 0" }}>
        <p style={{
          fontFamily: "Unbounded, sans-serif",
          fontWeight: 900,
          fontSize: "clamp(4rem, 15vw, 10rem)",
          letterSpacing: "-0.04em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.04)",
          lineHeight: 1,
          textAlign: "center",
          userSelect: "none",
        }}>
          REBELKY
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* REBELTEAM signup — hero block */}
        <div className="mb-12 pb-10 border-b border-white/5" data-testid="newsletter-block">
          <NewsletterSignup />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <span style={{
              fontFamily: "Unbounded, sans-serif",
              fontWeight: 900,
              fontSize: "1.5rem",
              letterSpacing: "-0.02em",
              color: "#FF005A",
            }}>REBELKY</span>
            <p className="mt-3" style={{ color: "#A1A1AA", fontSize: "0.9rem", lineHeight: 1.7 }}>
              Sofi · Agi · Leyla · Naty
            </p>
            <div className="flex gap-4 mt-4">
              <a
                href="https://www.youtube.com/@kapela_rebelky"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                style={{ color: "#52525B", transition: "color 0.2s" }}
                className="hover:text-white"
              >
                <Youtube size={20} />
              </a>
              <a
                href="https://open.spotify.com/artist/1igMI4JGH7TXcozyMzP48M"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-spotify"
                style={{ color: "#52525B", transition: "color 0.2s" }}
                className="hover:text-white"
              >
                <Music size={20} />
              </a>
              <a
                href="https://www.tiktok.com/@rebelky_official"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                style={{ color: "#52525B", transition: "color 0.2s" }}
                className="hover:text-white"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.54V6.78a4.85 4.85 0 01-1.02-.09z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/rebelky_official/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                style={{ color: "#52525B", transition: "color 0.2s" }}
                className="hover:text-white"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.65rem", letterSpacing: "0.2em", color: "#FF005A", fontWeight: 600, textTransform: "uppercase", marginBottom: "1rem" }}>
              NAVIGACE
            </p>
            <div className="flex flex-col gap-2">
              {[
                { to: "/aktuality", label: t.nav.news },
                { to: "/koncerty", label: t.nav.concerts },
                { to: "/merch", label: t.nav.merch },
                { to: "/o-nas", label: t.nav.about },
              ].map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{ color: "#A1A1AA", fontSize: "0.9rem", transition: "color 0.2s", textDecoration: "none" }}
                  className="hover:text-white"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://rebelky.online/api/uploads/Rebelky_Press_Kit.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-pdf-link"
                style={{
                  color: "#FF005A",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  transition: "color 0.2s",
                  textDecoration: "none",
                  marginTop: "0.4rem",
                }}
                className="hover:text-white"
              >
                <span aria-hidden style={{ fontSize: "0.7rem" }}>★</span> Press kit pro produkce (PDF)
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.65rem", letterSpacing: "0.2em", color: "#FF005A", fontWeight: 600, textTransform: "uppercase", marginBottom: "1rem" }}>
              {t.footer.contact.toUpperCase()}
            </p>
            <div style={{ color: "#A1A1AA", fontSize: "0.9rem", lineHeight: 2 }}>
              <p data-testid="footer-booking">{booking}</p>
              <p><a href={telHref(phone)} style={{ color: "#A1A1AA" }} data-testid="footer-phone">{phone}</a></p>
              <p><a href={`mailto:${bookingEmail}`} style={{ color: "#A1A1AA" }} data-testid="footer-booking-email">{bookingEmail}</a></p>
              {pressName && (
                <p className="mt-3" data-testid="footer-press">
                  <span style={{ color: "#FF005A", fontSize: "0.65rem", letterSpacing: "0.15em", display: "block", textTransform: "uppercase", fontWeight: 700 }}>Press</span>
                  {pressName}
                  {pressPhone && (
                    <>
                      <br />
                      <a href={telHref(pressPhone)} style={{ color: "#A1A1AA" }} data-testid="footer-press-phone">{pressPhone}</a>
                    </>
                  )}
                  {pressEmail && (
                    <>
                      <br />
                      <a href={`mailto:${pressEmail}`} style={{ color: "#A1A1AA" }} data-testid="footer-press-email">{pressEmail}</a>
                    </>
                  )}
                </p>
              )}
              <p className="mt-2" data-testid="footer-production">{production}</p>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
          <p style={{ color: "#52525B", fontSize: "0.8rem" }}>
            © {new Date().getFullYear()} Rebelky · {t.footer.allRights}
          </p>
          <div className="flex items-center gap-4 flex-wrap" style={{ fontSize: "0.78rem" }}>
            <Link to="/obchodni-podminky" data-testid="footer-terms" style={{ color: "#A1A1AA", textDecoration: "none" }} className="hover:text-white">
              Obchodní podmínky
            </Link>
            <span style={{ color: "#27272A" }}>·</span>
            <Link to="/gdpr" data-testid="footer-gdpr" style={{ color: "#A1A1AA", textDecoration: "none" }} className="hover:text-white">
              GDPR
            </Link>
            <span style={{ color: "#27272A" }}>·</span>
            <Link to="/doruceni" data-testid="footer-shipping" style={{ color: "#A1A1AA", textDecoration: "none" }} className="hover:text-white">
              Doručení
            </Link>
            <span style={{ color: "#27272A" }}>·</span>
            <span style={{ color: "#52525B" }}>Pinkofein s.r.o.</span>
          </div>
        </div>

        {/* Sponzor & tvůrce webu */}
        <div className="mt-4 pt-4" style={{ borderTop: "1px dashed rgba(255,255,255,0.04)", textAlign: "center" }} data-testid="footer-sponsor">
          <p style={{ color: "#52525B", fontSize: "0.72rem", letterSpacing: "0.08em", fontFamily: "Manrope, sans-serif" }}>
            Tvorba webu &amp; sponzor:{" "}
            <a
              href="https://www.solar-green.cz"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
              style={{ color: "#1DB954", fontWeight: 700, textDecoration: "none", letterSpacing: "0.1em" }}
            >
              SOLAR GREEN s.r.o.
            </a>
            <span style={{ color: "#27272A", margin: "0 0.5rem" }}>·</span>
            <span style={{ color: "#3F3F46" }}>fotovoltaické systémy &amp; e-commerce dev</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
